import classNames from 'classnames'
import { Spinner } from 'components/Spinner'
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { RippleEffect } from './RippleEffect'
import { useSettings } from 'hooks/settings'
import { BreakoutTooltip } from './BreakoutTooltip'
import { inE2E } from 'config/environment'
import { NumberBubble } from 'components/NumberBubble'
import { useTranslationTyped } from 'i18n/i18n'

const inPlaywright = inE2E()

export type ButtonKind =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'accent'
  | 'error'
  | 'success'
type ButtonVariant = 'text' | 'outlined' | 'contained'
type ButtonSize = 'small' | 'medium' | 'large'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  kind?: ButtonKind
  variant?: ButtonVariant
  size?: ButtonSize
  ripple?: boolean
  children?: React.ReactNode
  fullWidth?: boolean
  icon?: React.ReactNode
  loading?: boolean
  loadingText?: string
  tooltip?: string
  tooltipOpen?: boolean
  iconOnRight?: boolean
  allowDoubleClick?: boolean
  tooltipTransitionDuration?: number
  badgeCount?: number | string
  badgeSrLabel?: string
  as?: React.ElementType
  /**
   * When true, calls `event.stopPropagation()` before invoking `onClick`.
   * Useful when the button is nested inside a clickable container (e.g. a
   * card) and the click should not also trigger the container's handler.
   */
  stopPropagation?: boolean
}

const baseClass = 'select-none focus-ring-double'
const kinds: Record<ButtonKind, string> = {
  primary:
    'bg-core-primary text-core-on-primary hover:opacity-80 disabled:opacity-30',
  secondary:
    'bg-core-secondary text-core-on-secondary hover:bg-neutral-100 disabled:opacity-30',
  tertiary: 'bg-core-tertiary text-core-on-tertiary disabled:opacity-30',
  accent: 'bg-fixed-accent-color text-core-on-secondary disabled:opacity-30',
  error: 'bg-core-error text-core-on-error disabled:opacity-30',
  success: 'bg-core-success text-core-on-success disabled:opacity-30',
}

const rippleEffects: Record<ButtonKind, string> = {
  primary: 'light',
  secondary: 'dark',
  tertiary: 'dark',
  error: 'light',
  accent: 'light',
  success: 'light',
}

const sizes: Record<ButtonSize, string> = {
  large:
    'w-fit h-fit min-h-13 min-w-30 md:min-w-38 px-3 md:px-5 py-1 text-xs md:text-sm font-semibold leading-[normal] rounded-2xl',
  medium:
    'w-fit h-fit min-w-38 min-h-10 px-5 py-1 text-xs font-semibold leading-[normal] rounded-xl',
  small:
    'w-fit h-fit min-h-8 px-5 py-1 text-[10px] font-semibold leading-[normal] rounded-xl',
}

const sizesIconOnly: Record<ButtonSize, string> = {
  large: 'w-13 h-13 rounded-xl',
  medium: 'w-10 h-10 rounded-xl',
  small: 'w-8 h-8 rounded-xl',
}

export const BreakoutButton = forwardRef<HTMLButtonElement | null, ButtonProps>(
  function BreakoutButton(
    {
      kind,
      size = 'medium',
      badgeCount,
      badgeSrLabel,
      children,
      ripple,
      icon,
      iconOnRight,
      fullWidth,
      loading,
      type,
      tooltip,
      tooltipTransitionDuration,
      loadingText,
      onKeyDown,
      tooltipOpen,
      allowDoubleClick,
      stopPropagation,
      as: Component = 'button',
      ...otherProps
    }: ButtonProps,
    forwardedRef: React.Ref<HTMLButtonElement | null>
  ) {
    const { animationsEnabled } = useSettings()
    const { tt } = useTranslationTyped()
    const iconOnly = !children && icon
    const rippleEffect = useMemo(() => {
      if (!animationsEnabled) return undefined
      const rippleOrDefault = ripple !== undefined ? ripple : true
      return rippleOrDefault ? new RippleEffect() : undefined
    }, [ripple, animationsEnabled])
    const kindOrDefault = kind || 'primary'
    const kindRef = useRef(kindOrDefault)
    const elementRef = useRef<HTMLButtonElement>(null)

    useImperativeHandle(forwardedRef, () => elementRef.current, [])

    const badgeCountInt = useMemo(() => {
      if (badgeCount === undefined) return undefined
      if (typeof badgeCount === 'number') return badgeCount
      const parsed = parseInt(badgeCount, 10)
      return isNaN(parsed) ? undefined : parsed
    }, [badgeCount])

    const className = useMemo(() => {
      const classes: string[] = [baseClass]

      classes.push(kinds[kindOrDefault])

      if (iconOnly) {
        classes.push(sizesIconOnly[size])
      } else {
        classes.push(sizes[size])
      }

      if (icon || loading) {
        classes.push('inline-flex items-center justify-center gap-1')
      }

      if (loading) {
        classes.push('cursor-default')
      }

      if (fullWidth) {
        classes.push('w-full!')
      }

      return classes.join(' ')
    }, [size, icon, iconOnly, loading, fullWidth, kindOrDefault])

    const spinnerSize = useMemo(() => {
      if (size === 'small') return 0.8
      if (size === 'medium') return 1.1
      return 1.3
    }, [size])

    useEffect(() => {
      kindRef.current = kindOrDefault
    }, [kindOrDefault])

    const rippleWrapperRef = useRef<HTMLSpanElement>(null)

    const onMouseUp = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (loading) return
        if (rippleEffect) {
          // Use requestAnimationFrame to respond to kind change
          // When someone clicks and we change kind, we also change colors
          // which means we need a different effect. The way to do it is to
          // use requestAnimationFrame to wait for the next frame to get the
          // updated element and kind
          requestAnimationFrame(() => {
            if (elementRef.current) {
              rippleEffect.create(
                elementRef.current,
                event,
                rippleEffects[kindRef.current],
                rippleWrapperRef.current ?? undefined
              )
            }
          })
        }
      },
      [rippleEffect, loading, kindRef]
    )

    const doubleClickProtector = useRef(false)

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (!inPlaywright && !allowDoubleClick) {
          if (doubleClickProtector.current) return
          doubleClickProtector.current = true
          setTimeout(() => {
            doubleClickProtector.current = false
          }, 250)
        }

        // Prevent click event if button is loading
        if (loading) return
        if (stopPropagation) e.stopPropagation()
        if (otherProps.onClick) {
          otherProps.onClick(e)
        }
      },
      [loading, otherProps, allowDoubleClick, stopPropagation]
    )

    // The tooltip only reaches assistive tech while it is open, so an
    // icon-only button falls back to it for its accessible name.
    const ariaLabel =
      otherProps['aria-label'] ??
      (iconOnly && !otherProps['aria-labelledby'] ? tooltip : undefined)
    // A disabled button can't take focus, so its tooltip never opens for
    // keyboard or screen reader users. Keep the reason in the DOM instead.
    const disabledReason =
      otherProps.disabled && tooltip && tooltip !== ariaLabel
        ? tooltip
        : undefined
    // An aria-label replaces the button's content as its name, so a labelled
    // button gets the reason as a description instead.
    const disabledReasonId = useId()
    const describeDisabledReason = disabledReason && ariaLabel
    const ariaDescribedBy = describeDisabledReason
      ? [otherProps['aria-describedby'], disabledReasonId]
          .filter(Boolean)
          .join(' ')
      : otherProps['aria-describedby']

    let component = (
      <Component
        {...otherProps}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        type={type || 'button'}
        ref={elementRef}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onMouseUp={onMouseUp}
        className={classNames(
          className,
          otherProps.className,
          rippleEffect && 'relative'
        )}
      >
        {rippleEffect !== undefined && (
          <span
            ref={rippleWrapperRef}
            className="pointer-events-none absolute inset-0 overflow-hidden"
            style={{ borderRadius: 'inherit' }}
            aria-hidden
          />
        )}
        {loading && (
          <Spinner
            aria-hidden
            size={spinnerSize}
            className={classNames({
              'on-primary': kind === 'primary',
              'on-secondary': kind === 'secondary' || kind === 'accent',
              'on-tertiary': kind === 'tertiary',
              'on-error': kind === 'error',
              'on-success': kind === 'success',
            })}
          />
        )}
        {loading && (
          <span className="ml-2">
            {loadingText || tt.common.loading_and_dots()}
          </span>
        )}
        {!loading && (
          <>
            {icon && !iconOnRight && (
              <span className="inline-block">{icon}</span>
            )}
            {children}
            {icon && iconOnRight && (
              <span className="inline-block">{icon}</span>
            )}
          </>
        )}
        {disabledReason &&
          (describeDisabledReason ? (
            <span id={disabledReasonId} className="sr-only">
              {disabledReason}
            </span>
          ) : (
            <span className="sr-only">, {disabledReason}</span>
          ))}
      </Component>
    )

    if (badgeCount !== undefined) {
      component = (
        <span className="relative">
          {badgeCountInt !== undefined && (
            <NumberBubble
              count={badgeCountInt}
              srLabel={badgeSrLabel || ''}
              className={classNames('absolute z-10', {
                '-top-2 -right-2': size === 'medium' || size === 'large',
                '-top-1.75 -right-1.75': size === 'small',
              })}
            />
          )}
          {component}
        </span>
      )
    }

    if (tooltip) {
      return (
        <BreakoutTooltip
          forceOpen={tooltipOpen}
          content={tooltip}
          transitionDuration={tooltipTransitionDuration}
        >
          {component}
        </BreakoutTooltip>
      )
    } else {
      return component
    }
  }
)

export function BreakoutIconButton(
  props: Omit<ButtonProps, 'size' | 'iconOnRight'> & {
    size?: 'large' | 'small'
  }
) {
  return <BreakoutButton {...props} />
}

type AsyncButtonProps = ButtonProps & {
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<void>
}

export const BreakoutAsyncButton = ({
  onClick,
  loading,
  children,
  ...rest
}: AsyncButtonProps) => {
  const [requestInProgress, setRequestInProgress] = useState(false)
  return (
    <BreakoutButton
      {...rest}
      loading={requestInProgress || loading}
      onClick={async (e) => {
        setRequestInProgress(true)
        try {
          await onClick(e)
        } finally {
          setRequestInProgress(false)
        }
      }}
    >
      {children}
    </BreakoutButton>
  )
}

export function BreakoutButtonLink({
  href,
  className,
  size,
  kind,
  target,
  rel,
  children,
  onClick,
  testId,
}: {
  href: string
  size?: ButtonSize
  kind?: ButtonKind
  target?: string
  rel?: string
  className?: string
  children: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  testId?: string
}) {
  const sizeOrDefault = size || 'medium'
  const kindOrDefault = kind || 'primary'

  return (
    <a
      className={classNames(
        baseClass,
        kinds[kindOrDefault],
        sizes[sizeOrDefault],
        'inline-flex items-center justify-center gap-1',
        className
      )}
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </a>
  )
}
